"use client";

import { motion, useReducedMotion } from "framer-motion";
import { BotanicalLeaf } from "@/components/decorative/BotanicalLeaf";
import { PaperTextureOverlay } from "@/components/decorative/PaperTextureOverlay";
import { WatercolorBlob } from "@/components/decorative/WatercolorBlob";
import { useMouseParallax } from "@/hooks/useMouseParallax";
import { cn } from "@/lib/utils";

interface PageDecorLayerProps {
  variant?: "hero" | "section";
  palette?: "contact" | "site";
  className?: string;
}

export function PageDecorLayer({
  variant = "hero",
  palette = "contact",
  className,
}: PageDecorLayerProps) {
  const reduced = useReducedMotion();
  const { x, y } = useMouseParallax({ strength: 0.018, maxOffset: 10 });
  const animate = !reduced;
  const isHero = variant === "hero";
  const heroGradient =
    palette === "site" ? "site-hero-gradient" : "contact-hero-gradient";
  const sectionGradient =
    palette === "site" ? "site-section-gradient" : "contact-section-gradient";

  return (
    <div
      aria-hidden
      className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}
    >
      <PaperTextureOverlay />

      <div
        className={cn(
          "absolute inset-0",
          isHero ? heroGradient : sectionGradient
        )}
      />

      {isHero && (
        <motion.div
          style={{ x: animate ? x * 0.5 : 0, y: animate ? y * 0.5 : 0 }}
          className="absolute -left-24 -top-16 h-72 w-72 opacity-60 md:h-96 md:w-96"
        >
          <WatercolorBlob variant="orange" animate={animate} className="h-full w-full" />
        </motion.div>
      )}

      <motion.div
        style={{ x: animate ? x * -0.3 : 0, y: animate ? y * -0.3 : 0 }}
        className={cn(
          "absolute -bottom-20 -right-20 opacity-50",
          isHero ? "h-80 w-80 md:h-[28rem] md:w-[28rem]" : "h-56 w-56 md:h-72 md:w-72"
        )}
      >
        <WatercolorBlob variant="green" animate={animate} className="h-full w-full" />
      </motion.div>

      <BotanicalLeaf
        animate={animate}
        className="absolute left-2 top-8 h-20 w-14 opacity-60 md:left-6 md:top-12 md:h-28 md:w-[4.5rem]"
      />

      <BotanicalLeaf
        animate={animate}
        flip
        className="absolute bottom-12 right-2 hidden h-20 w-14 opacity-50 md:block md:bottom-16 md:right-8 md:h-28 md:w-[4.5rem]"
      />
    </div>
  );
}
